import { ActionTree } from 'vuex';
import { Actions } from '@/types/store/actions';
import { NumberOption, State } from '@/types/store/state';
import { MutationTypes } from '@/store/mutations';
import { AppInitialOptions, DataObject } from '@/types/api';
import * as api from '@/helpers/api';
import router, { addRoutes } from '@/router';
import { Routes } from '@/types/router';
import { Cookie } from '@/helpers/cookie';

export enum ActionTypes {
    SetInitialOptions = 'SET_INITIAL_OPTIONS',
    PreFillDetails = 'PRE_FILL_DETAILS',
    Subscribe = 'SUBSCRIBE',
}

export const actions: ActionTree<State, State> & Actions = {
    [ActionTypes.SetInitialOptions] ({ commit, dispatch }, json) {
        const options = JSON.parse(json) as AppInitialOptions;
        addRoutes(options.routes[Routes.GetUpdates], options.routes[Routes.ConfirmView]);
        commit(MutationTypes.SetSiteName, options.siteName);
        commit(MutationTypes.SetApiUrl, options.apiUrl);
        const frequencies = objectToNumberOptionArray(options.contactSettings.frequency);
        const periods = objectToNumberOptionArray(options.contactSettings.period);
        commit(MutationTypes.SetFrequencies, frequencies);
        commit(MutationTypes.SetPeriods, periods);
        commit(MutationTypes.SetSearches, options.availableSearches);
        commit(MutationTypes.SetCurrentSearch, options.currentSearch);
        commit(MutationTypes.SetNotificationFrequency, getDefaultOption(frequencies)?.value);
        commit(MutationTypes.SetNotificationPeriod, getDefaultOption(periods)?.value);
        commit(MutationTypes.SetShowIncludeUk, options.showIncludeUk);
        dispatch(ActionTypes.PreFillDetails);
    },
    [ActionTypes.PreFillDetails] ({ commit }) {
        const { email, frequency, period } = Cookie.get();
        email && commit(MutationTypes.SetEmail, email);
        frequency && commit(MutationTypes.SetNotificationFrequency, frequency);
        period && commit(MutationTypes.SetNotificationPeriod, period);
        if (email && frequency && period) {
            commit(MutationTypes.SetRememberDetails, true);
        }
    },
    async [ActionTypes.Subscribe] ({ commit, getters, state }) {
        try {
            commit(MutationTypes.SetIsSubmitting, true);
            commit(MutationTypes.SetError, '');
            commit(MutationTypes.SetIsEmailError, false);

            const currentSearch = state.searchForm.currentSearch;
            const searchTypes = state.searchForm.searches;
            const searchType = searchTypes.find(searchType => +searchType.id === currentSearch);
            const searchTypeKey = searchType ? searchType.key : null;
            const includeUk = searchTypeKey && ['make', 'carClass'].includes(searchTypeKey)
                ? state.searchForm.include_uk
                : null;

            const response = await api.subscribe(state.apiUrl, {
                formFields: getters.getSelectedFiltersForRequest(),
                searchTypeId: currentSearch,
                frequencyId: getters.getNotificationData.frequency,
                periodId: getters.getNotificationData.period,
                email: state.email,
                includeUk: includeUk
            });
            commit(MutationTypes.SetIsSubmitting, false);
            if (response.success) {
                if (state.rememberDetails) {
                    Cookie.set({
                        email: state.email,
                        period: getters.getNotificationData.period,
                        frequency: getters.getNotificationData.frequency
                    });
                } else {
                    Cookie.unset();
                }
                await router.push({ name: Routes.ConfirmView });
            } else {
                commit(MutationTypes.SetError, response.error);
                if (response.field === 'email') {
                    commit(MutationTypes.SetIsEmailError, true);
                }
            }
        } catch (e) {
            commit(MutationTypes.ApiErrorOccurred);
            commit(MutationTypes.SetError, 'Please try again later.');
        }
    }
};

function objectToNumberOptionArray (data: DataObject): Array<NumberOption> {
    const options: Array<NumberOption> = [];
    for (const id in data) {
        if (Object.prototype.hasOwnProperty.call(data, id)) {
            options.push({
                value: parseInt(id, 10),
                caption: data[id].caption,
                isDefault: data[id].isDefault,
                interval: +data[id].interval
            } as NumberOption);
        }
    }
    return options;
}

function getDefaultOption (data: Array<NumberOption>): NumberOption {
    return data.filter(item => item.isDefault === true)[0];
}
